const NO_USABLE_DEVICES = "Couldn't find authenticating device for user";
const DAILY_LIMIT_EXCEEDED = 'Daily limit of SMS authentication attempts has been exceeded';
const REQUEST_FAILED_400 = 'Request failed with status code 400';

import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getIsMfaLoading } from '@/features/login/selectors';
import { mfaSignIn, removeDevice, resetMfaQuota, verifyOtp } from '@/features/login/slice'; // Import mfaSignIn and removeDevice from login slice
import { ERROR_VERIFYING_OTP } from '@/features/mfa/constants';
import { selectFlowId, selectIsMfaCompleted } from '@/features/mfa/selectors';
import { resetOtpRequested } from '@/features/mfa/slice';
import { selectMfaDevice } from '@/features/mfaDevices/selectors';
import { selectUser } from '@/features/user/selectors';
import { useEffectOnceWhen } from '@/hooks/useEffectOnceWhen';
import { withAuthLayout } from '@/layouts/Auth';

import { Action } from '@bypasslane/clover--web-lib-sprout-action';
import { Card, CardBody, CardHeader, CardSubtitle } from '@bypasslane/clover--web-lib-sprout-card';
import { FormGroup } from '@bypasslane/clover--web-lib-sprout-form-group';
import { OTPTextField } from '@bypasslane/clover--web-lib-sprout-otp-text-field';
import parsePhoneNumber, { isPossiblePhoneNumber } from 'libphonenumber-js';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import MfaIcon from './MfaIcon';

const Legend = ({ device = null, flowId = null, shouldShowRequestButton = false }) => {
  if (!device) {
    return 'Verification Code';
  }

  const formattedPhone = useMemo(() => {
    // Check if the phone number is valid
    const phoneNumber = parsePhoneNumber(device?.phone);
    if (!phoneNumber) {
      return null;
    }
    return isPossiblePhoneNumber(device?.phone, 'US')
      ? phoneNumber.formatNational()
      : phoneNumber.formatInternational();
  }, [device]);

  const conditionalMfaMessagye = useMemo(() => {
    if (!flowId && shouldShowRequestButton) {
      return formattedPhone
        ? `A code will be sent to ${formattedPhone}`
        : 'A code will be sent to your device';
    }

    if (!flowId && !shouldShowRequestButton) {
      return 'Checking MFA status...';
    }

    return formattedPhone ? `We sent a code to ${formattedPhone}` : 'We sent a code to your device';
  }, [flowId, formattedPhone]);

  return (
    <div className="border-b border-gray-200 pb-2 mb-2 flex items-center">
      <MfaIcon device={device} className="w-6 h-6" color="#228800" />
      {conditionalMfaMessagye}
    </div>
  );
};

const MfaVerifyForm = () => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState('');
  const [formResponse, setFormResponse] = useState({});
  const otpInputRef = useRef(null);
  const mfaCompleted = useSelector(selectIsMfaCompleted);
  const [hasRequestedNewCode, setHasRequestedNewCode] = useState(false);

  useEffect(() => {
    if (otpInputRef.current?.input) {
      otpInputRef.current.input.focus();
    }
  }, []);

  useEffectOnceWhen(() => {
    dispatch(mfaSignIn());
    setHasRequestedNewCode(true);
  }, [!mfaCompleted, !hasRequestedNewCode]);

  const flowId = useSelector(selectFlowId); // Get flowId from state
  const isPending = formResponse?.pending;
  const otpError = useSelector((state) => state.auth.mfaError);
  const userOtpError = useSelector((state) => state.auth.user.error);
  const authError = useSelector((state) => state.auth.error);
  const user = useSelector(selectUser);
  const device = useSelector(selectMfaDevice);
  const isDisabled =
    formResponse?.mfa_status === 'QUOTA_EXCEEDED' ||
    formResponse?.mfa_status === 'OTP_BLOCKED' ||
    (otpError?.code === 'NO_USABLE_DEVICES' && !otpError?.message?.startsWith(NO_USABLE_DEVICES)) ||
    (userOtpError?.code === 'NO_USABLE_DEVICES' &&
      !userOtpError?.message?.startsWith(NO_USABLE_DEVICES)) ||
    isPending;

  const safeError =
    (!otpError?.message?.startsWith(NO_USABLE_DEVICES) && otpError?.message) ||
    (!userOtpError?.message?.startsWith(NO_USABLE_DEVICES) && userOtpError?.message) ||
    formResponse?.error_message ||
    formResponse?.error?.message ||
    (authError === REQUEST_FAILED_400 && ERROR_VERIFYING_OTP) ||
    authError;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await dispatch(verifyOtp({ otpCode: otp, flowId }));
    setFormResponse(response.payload);
  };

  const handleGetNewCode = async () => {
    dispatch(resetOtpRequested()); // Reset otpRequested
    const response = await dispatch(mfaSignIn());
    setFormResponse(response.payload);
  };

  const handleResetQuota = () => {
    dispatch(resetMfaQuota());
  };

  const isMfaLoading = useSelector(getIsMfaLoading);

  const hasRecentlyAddedDeviceFromStorage = (() => {
    const storedData = sessionStorage.getItem('hasRecentlyAddedDevice');
    if (storedData) {
      const { value, expiration } = JSON.parse(storedData);
      if (Date.now() < expiration) {
        return value; // Return true if not expired
      } else {
        sessionStorage.removeItem('hasRecentlyAddedDevice'); // Remove expired key
      }
    }
    return false;
  })();

  const hasRecentlyAddedDevice =
    useSelector((state) => state.mfa.hasRecentlyAddedDevice) || hasRecentlyAddedDeviceFromStorage;

  console.log('MfaVerifyForm', { flowId, isPending, otpError, userOtpError, authError });

  return (
    <Card>
      <CardHeader title="Verify Your Device" />
      <CardSubtitle></CardSubtitle>
      <CardBody>
        <FormGroup errorText={safeError} legend={<Legend device={device} flowId={flowId} />}>
          {flowId ? ( // Check if flowId exists
            <form onSubmit={handleSubmit} method="POST" className="space-y-6">
              <div className="login__field">
                <OTPTextField
                  ref={otpInputRef}
                  className="login__field"
                  label="One Time Passcode"
                  name="otp"
                  required
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={otpError === 'NO_USABLE_DEVICES' || isPending} // Updated to match error handling
                />
              </div>
              <div>
                <Action
                  fullwidth
                  variant="primary"
                  disabled={isDisabled}
                  type="submit"
                  loading={isMfaLoading}
                >
                  Submit
                </Action>
              </div>
            </form>
          ) : (
            <div>
              <Action fullwidth variant="primary" onClick={handleGetNewCode} loading={isMfaLoading}>
                Request Code
              </Action>
            </div>
          )}
        </FormGroup>
        {!isDisabled &&
          flowId && ( // Ensure this section only shows if flowId exists
            <div className="my-4">
              <div className="text-sm leading-6">
                <Action
                  fullwidth
                  variant="flat"
                  className="font-semibold text-gray-600 hover:text-gray-500"
                  onClick={handleGetNewCode}
                >
                  Didn&apos;t get a code? Request a new one
                </Action>
              </div>
            </div>
          )}
        {user?.super_admin && safeError === DAILY_LIMIT_EXCEEDED && (
          <div className="text-sm leading-6 mt-4">
            <Action
              fullwidth
              variant="flat"
              className="font-semibold text-gray-600 hover:text-gray-500"
              onClick={handleResetQuota}
            >
              Reset Quota
            </Action>
          </div>
        )}
        {hasRecentlyAddedDevice && ( // Protect the Go Back button
          <div className="mt-4">
            <Action
              fullwidth
              variant="flat"
              className="font-semibold text-gray-600 hover:text-gray-500"
              onClick={() => dispatch(removeDevice(device))} // Dispatch removeDevice
              destructive
            >
              Go Back
            </Action>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default withAuthLayout(MfaVerifyForm, {
  enableProfiler: true,
  enableErrorBoundary: true
});

Legend.propTypes = {
  device: PropTypes.shape({
    phone: PropTypes.string.isRequired
  }),
  flowId: PropTypes.string,
  shouldShowRequestButton: PropTypes.bool
};
Legend.displayName = 'Legend';
MfaVerifyForm.displayName = 'MfaVerifyForm';
